import {
  Icon, Table as ChakraTable, Text,
} from '@chakra-ui/react';
import { Star, UsersRound } from 'lucide-react';
import React, { useMemo } from 'react';

import AvatarListItem from '@app/components/chakra/avatar-list-item';
import Table from '@app/components/chakra/table';
import UserRow from '@app/components/chakra/user-row';
import { type Group } from '@app/types/api/group';
import { type User } from '@app/types/api/user';
import { Avatar } from '@chakra-snippets/avatar';

interface MemberRowProps {
  member: User;
  isLead?: boolean;
}

const MemberRow: React.FC<MemberRowProps> = ({ member, isLead }) => (
  <ChakraTable.Row>
    <ChakraTable.Cell id="lead" w="30px">
      {isLead ? <Icon color="orange.500"><Star fill="currentColor" /></Icon> : ''}
    </ChakraTable.Cell>
    <ChakraTable.Cell id="name" maxWidth={250}>
      <UserRow
        showTitle
        showEmail={false}
        option={{ ...member, ...member.profile }}
        borderRadius="full"
      />
    </ChakraTable.Cell>
    <ChakraTable.Cell id="email">
      <Text>{member.email}</Text>
    </ChakraTable.Cell>
    <ChakraTable.Cell id="last-active">
      <Text>{member.lastLoginTime}</Text>
    </ChakraTable.Cell>
  </ChakraTable.Row>
);

interface MemberTabProps {
  members: User[];
  leads: User[];
  subgroups: Group[];
  count: number;
}

const MemberTab: React.FC<MemberTabProps> = ({
  members,
  leads,
  subgroups,
  count,
}) => {
  const rows = useMemo(() => {
    const leadRows = leads?.map((lead: User) => (
      <MemberRow key={lead.id} member={lead} isLead />
    )) || [];
    const memberRows = members?.map((member: User) => (
      <MemberRow key={member.id} member={member} />
    )) || [];
    const allRows = [...leadRows, ...memberRows];

    if (subgroups?.length) {
      allRows.push(
        <ChakraTable.Row>
          <ChakraTable.Cell />
          <ChakraTable.Cell>
            <AvatarListItem
              avatar={<Avatar backgroundColor="gray.300" size="md" fallback={<UsersRound />} icon={<UsersRound />} />}
              name={`${count - members.length - leads.length} Other Members`}
              subtitles={[`${subgroups?.length ?? 0} Sub-Group${(subgroups?.length ?? 0) === 1 ? '' : 's'}`]}
              showSubtitle
            />
          </ChakraTable.Cell>
          <ChakraTable.Cell />
          <ChakraTable.Cell />
        </ChakraTable.Row>,
      );
    }

    return allRows;
  }, [members, leads, subgroups, count]);

  return (
    <Table
      type="members"
      rows={rows}
      columns={[
        {
          id: 'lead',
        },
        {
          id: 'name',
          label: 'Name',
        },
        {
          id: 'email',
          label: 'Email',
        },
        {
          id: 'last-active',
          label: 'Last Active',
        },
      ]}
      label="Members"
    />
  );
};

export default MemberTab;
